import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { getStudent } from '../services/auth';

const STAGE_LABEL  = { verified: 'Lead Verified', visa_approved: 'Visa Approved', enrolled: 'Enrolled' };
const STAGE_AMOUNT = { verified: 1000, visa_approved: 9000, enrolled: 30000 };

export default function Dashboard() {
  const student = getStudent();
  const [stats, setStats]           = useState(null);
  const [referrals, setReferrals]   = useState([]);
  const [loading, setLoading]       = useState(true);

  // Refer a Friend form
  const [invite, setInvite]               = useState({ name: '', phone: '', email: '' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError]     = useState('');
  const [inviteSuccess, setInviteSuccess] = useState(null); // holds submitted { name, phone } on success

  const load = useCallback(async () => {
    try {
      const [sRes, rRes] = await Promise.all([
        api.get('/referral/student/referrals/stats'),
        api.get('/referral/student/referrals/'),
      ]);
      setStats(sRes.data);
      setReferrals(rRes.data.slice(0, 5));
    } catch {/* silent */} finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleInviteChange = e => setInvite(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleInvite = async e => {
    e.preventDefault();
    setInviteError(''); setInviteSuccess(null);
    if (!invite.name || !invite.phone) { setInviteError('Name and phone are required'); return; }
    setInviteLoading(true);
    try {
      await api.post('/referral/student/referrals/submit', invite);
      setInviteSuccess({ name: invite.name, phone: invite.phone.replace(/\D/g, '') });
      setInvite({ name: '', phone: '', email: '' });
      load(); // refresh stats + recent referrals
    } catch (err) {
      setInviteError(err.response?.data?.detail || 'Failed to add referral. Please try again.');
    } finally { setInviteLoading(false); }
  };

  const sendWhatsApp = () => {
    if (!inviteSuccess) return;
    const message = `Hi ${inviteSuccess.name.split(' ')[0]}! 👋\n\n*${student?.name}* has referred you to UNI GLOBAL Education Consultancy for your study abroad journey.\n\nOur team will reach out to guide you through the process.\n\n📞 Contact us: +91 9177454500`;
    window.open(`https://wa.me/${inviteSuccess.phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const STATUS_STEP  = { lead: 0, verified: 1, visa_approved: 2, enrolled: 3 };
  const STEPS        = ['lead', 'verified', 'visa_approved', 'enrolled'];
  const STEP_LABELS  = { lead: 'Lead', verified: 'Verified', visa_approved: 'Visa', enrolled: 'Enrolled' };

  if (loading) return <div className="page-loading">Loading…</div>;

  return (
    <div className="dashboard">
      {/* Welcome */}
      <div className="dash-welcome">
        <div>
          <h1>Welcome, {student?.name?.split(' ')[0]}! 👋</h1>
          <p>Track your referrals and earn ₹40,000 per student.</p>
        </div>
      </div>

      {/* Refer a Friend */}
      <div className="section-card">
        <h3 style={{ marginBottom: '1rem' }}>🤝 Add a Referral</h3>
        <p className="invite-desc" style={{ marginBottom: '1rem' }}>
          Enter the student's details to add them as a referral lead instantly.
        </p>
        {inviteSuccess && (
          <div className="auth-info" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '.5rem' }}>
            <span>✅ <strong>{inviteSuccess.name}</strong> added as a referral lead!</span>
            <button onClick={sendWhatsApp} className="btn-copy" style={{ fontSize: '.82rem' }}>
              📲 Also send WhatsApp invite
            </button>
          </div>
        )}
        <form onSubmit={handleInvite} className="invite-form">
          <div className="invite-row">
            <div className="form-group">
              <label>Student's Full Name *</label>
              <input name="name" value={invite.name} onChange={handleInviteChange} placeholder="Priya Sharma" required />
            </div>
            <div className="form-group">
              <label>WhatsApp Number * <span className="code-hint">(with country code)</span></label>
              <input name="phone" value={invite.phone} onChange={handleInviteChange} placeholder="919876543210" required />
            </div>
          </div>
          <div className="invite-row">
            <div className="form-group">
              <label>Email ID</label>
              <input name="email" type="email" value={invite.email} onChange={handleInviteChange} placeholder="friend@example.com" />
            </div>
            <div className="form-group" style={{ justifyContent: 'flex-end', display: 'flex', alignItems: 'flex-end' }}>
              <button type="submit" className="btn-primary" disabled={inviteLoading} style={{ width: '100%' }}>
                {inviteLoading ? 'Adding…' : 'Add Referral'}
              </button>
            </div>
          </div>
          {inviteError && <div className="auth-error">{inviteError}</div>}
        </form>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <Link to="/referrals" className="stat-card" style={{ textDecoration: 'none', cursor: 'pointer' }}>
          <div className="stat-value">{stats?.total_referred ?? 0}</div>
          <div className="stat-label">🤝 Students Referred</div>
        </Link>
        <Link to="/rewards" className="stat-card stat-earned" style={{ textDecoration: 'none', cursor: 'pointer' }}>
          <div className="stat-value">₹{(stats?.total_earned ?? 0).toLocaleString()}</div>
          <div className="stat-label">Referral Earned</div>
        </Link>
        <Link to="/rewards" className="stat-card stat-pending" style={{ textDecoration: 'none', cursor: 'pointer' }}>
          <div className="stat-value">₹{(stats?.pending_amount ?? 0).toLocaleString()}</div>
          <div className="stat-label">Referral Pending</div>
        </Link>
      </div>

      {/* Reward Milestones */}
      <div className="section-card">
        <h3>Reward Structure</h3>
        <p style={{ color: '#64748b', fontSize: '.88rem', marginBottom: '1rem' }}>
          Earn ₹40,000 for every student you refer.
        </p>
        <div className="dash-reward-grid" style={{ display: 'grid', gap: '1rem' }}>
          <div>
            <div style={{ fontWeight: 700, color: '#1e293b', marginBottom: '.5rem', fontSize: '.9rem' }}>
              You earn (as referrer)
            </div>
            <div className="milestone-track" style={{ flexDirection: 'column', gap: '.5rem' }}>
              {Object.entries(STAGE_LABEL).map(([stage, label]) => (
                <div key={stage} className="milestone" style={{ flexDirection: 'row', gap: '.75rem', justifyContent: 'flex-start' }}>
                  <div className="milestone-dot" style={{ width: 56, height: 56, flexShrink: 0 }}>₹{(STAGE_AMOUNT[stage]/1000).toFixed(0)}K</div>
                  <div className="milestone-label" style={{ textAlign: 'left' }}>{label}</div>
                </div>
              ))}
              <div className="milestone milestone-total" style={{ flexDirection: 'row', gap: '.75rem', justifyContent: 'flex-start' }}>
                <div className="milestone-dot total" style={{ width: 56, height: 56, flexShrink: 0 }}>₹40K</div>
                <div className="milestone-label" style={{ textAlign: 'left' }}>Total Referral Reward</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Referrals */}
      {referrals.length > 0 && (
        <div className="section-card">
          <div className="section-header">
            <h3>Recent Referrals</h3>
            <Link to="/referrals" className="link-all">View All →</Link>
          </div>
          <div className="referral-list">
            {referrals.map(ref => {
              const step = STATUS_STEP[ref.status] ?? 0;
              return (
                <div key={ref.id} className="referral-row">
                  <div className="ref-info">
                    <strong>{ref.referred_name}</strong>
                    <span>{ref.referred_email}</span>
                  </div>
                  <div className="ref-progress">
                    {STEPS.map((s, i) => (
                      <span key={s} className={`step-dot ${i <= step ? 'done' : ''}`} title={STEP_LABELS[s]}>
                        {i <= step ? '●' : '○'}
                      </span>
                    ))}
                  </div>
                  <div className="ref-earned">
                    {ref.total_earned > 0 && <span className="earned-badge">₹{ref.total_earned.toLocaleString()} earned</span>}
                    {ref.pending_amount > 0 && <span className="pending-badge">₹{ref.pending_amount.toLocaleString()} pending</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {referrals.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h3>No referrals yet</h3>
          <p>Use the form above to add students you've referred.</p>
        </div>
      )}
    </div>
  );
}
